import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUserStore } from '../store/userStore';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../aws-config';

// Helper function to convert blob to base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = error => reject(error);
  });
};

interface FormData {
  id: string;
  name: string;
  isFeature: string;
  isInGallery: string;
  sqr_feet: string;
  highlight: string;
  type: string;
  completion_year: string;
  location: string;
  project_details: string;
}

interface ImageFile extends File {
  preview?: string;
  width?: number;
  height?: number;
}

const CreateWebProjectPage: React.FC = () => {
  const { isAdmin } = useUserStore();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    id: '',
    name: '',
    isFeature: 'no',
    isInGallery: 'no',
    sqr_feet: '',
    highlight: 'NONE',
    type: '',
    completion_year: '',
    location: '',
    project_details: ''
  });
  
  // Image upload state
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redirect if not admin
  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // Clean up image previews on unmount
  useEffect(() => {
    return () => {
      selectedImages.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [selectedImages]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle image selection
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setError(null);
    const newImages: ImageFile[] = [];
    
    for (let i = 0; i < e.target.files.length; i++) {
      const file = e.target.files[i] as ImageFile;
      
      // Create preview
      file.preview = URL.createObjectURL(file);
      
      // Check if image is landscape
      try {
        const dimensions = await getImageDimensions(file);
        file.width = dimensions.width;
        file.height = dimensions.height;
        
        if (dimensions.width < dimensions.height) {
          setError('All images must be in landscape format (width > height)');
        } else {
          newImages.push(file);
        }
      } catch (err) {
        console.error('Error checking image dimensions:', err);
        setError('Error checking image dimensions. Please try again.');
      }
    }
    
    setSelectedImages(prev => [...prev, ...newImages]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get image dimensions
  const getImageDimensions = (file: File): Promise<{width: number, height: number}> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Remove image from selection
  const handleRemoveImage = (index: number) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      if (newImages[index].preview) {
        URL.revokeObjectURL(newImages[index].preview!);
      }
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    console.log('=== FORM SUBMISSION STARTED ===');
    console.log('Form data:', formData);
    console.log('Selected images:', selectedImages.length);
    
    // Validate form
    if (!formData.name) {
      setError('Project name is required');
      console.log('Validation failed: Project name is required');
      return;
    }
    
    if (!formData.type) {
      setError('Project type is required');
      console.log('Validation failed: Project type is required');
      return;
    }
    
    if (selectedImages.length === 0) {
      setError('At least one image is required');
      console.log('Validation failed: At least one image is required');
      return;
    }
    
    // Check if any image is not landscape
    const nonLandscapeImage = selectedImages.find(img => img.width && img.height && img.width < img.height);
    if (nonLandscapeImage) {
      setError('All images must be in landscape format (width > height)');
      console.log('Validation failed: Non-landscape image found');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    
    try {
      // Get authentication token
      console.log('Fetching authentication token...');
      const authSession = await fetchAuthSession();
      console.log('Auth session received:', {
        hasTokens: !!authSession.tokens,
        hasIdToken: !!authSession.tokens?.idToken,
        hasAccessToken: !!authSession.tokens?.accessToken,
        identityId: authSession.identityId || 'none'
      });
      
      const { idToken } = authSession.tokens ?? {};
      
      if (!idToken) {
        console.error('Authentication failed: No ID token available');
        throw new Error('Not authenticated');
      }
      
      console.log('Authentication successful');
      
      // Create folder name (project name with spaces replaced by underscores)
      const folderName = formData.name.replace(/\s+/g, '_');
      console.log(`Creating folder: ${folderName}`);
      
      // Upload images
      const totalFiles = selectedImages.length + 1; // +1 for the data file
      let uploadedFiles = 0;
      
      console.log(`Starting upload of ${selectedImages.length} images...`);
      
      for (const image of selectedImages) {
        const fileName = `${folderName}/${image.name}`;
        console.log(`Preparing to upload image: ${fileName}`);
        
        try {
          console.log('Image details:', {
            name: image.name,
            type: image.type,
            size: image.size,
            dimensions: `${image.width}x${image.height}`
          });
          
          // Step 1: Split file into chunks
          const CHUNK_SIZE = 500 * 1024; // 500KB chunks to stay well under API Gateway limits
          const fileSize = image.size;
          const numChunks = Math.ceil(fileSize / CHUNK_SIZE);
          console.log(`Splitting file into ${numChunks} chunks of ${CHUNK_SIZE} bytes each`);
          
          // Step 2: Initialize chunked upload
          console.log('Initializing chunked upload...');
          const initResponse = await fetch(`${API_URL}/s3`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken.toString()}`
            },
            body: JSON.stringify({
              operation: 'initChunkedUpload',
              folderName: folderName,
              fileName: image.name,
              contentType: image.type,
              totalChunks: numChunks
            })
          });
          
          if (!initResponse.ok) {
            const errorData = await initResponse.json();
            throw new Error(`API error: ${errorData.error || initResponse.statusText}`);
          }
          
          const initResult = await initResponse.json();
          console.log('Chunked upload initialized:', initResult);
          
          if (!initResult.uploadId) {
            throw new Error('No upload ID received from API');
          }
          
          const uploadId = initResult.uploadId;
          
          // Step 3: Upload each chunk
          for (let i = 0; i < numChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(fileSize, start + CHUNK_SIZE);
            
            // Get chunk from file
            const chunk = image.slice(start, end);
            
            // Convert chunk to base64
            const base64Chunk = await blobToBase64(chunk);
            
            // Upload chunk
            console.log(`Uploading chunk ${i + 1}/${numChunks}...`);
            const chunkResponse = await fetch(`${API_URL}/s3`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken.toString()}`
              },
              body: JSON.stringify({
                operation: 'uploadChunk',
                uploadId: uploadId,
                folderName: folderName,
                fileName: image.name,
                chunkIndex: i,
                totalChunks: numChunks,
                chunk: base64Chunk
              })
            });
            
            if (!chunkResponse.ok) {
              const errorData = await chunkResponse.json();
              throw new Error(`API error: ${errorData.error || chunkResponse.statusText}`);
            }
            
            const chunkResult = await chunkResponse.json();
            console.log(`Chunk ${i + 1}/${numChunks} uploaded:`, chunkResult);
            
            // Update progress for this image
            const chunkProgress = Math.round((i + 1) / numChunks * 100);
            console.log(`Chunk progress: ${chunkProgress}%`);
          }
          
          // Step 4: Finalize upload
          console.log('Finalizing chunked upload...');
          const finalizeResponse = await fetch(`${API_URL}/s3`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken.toString()}`
            },
            body: JSON.stringify({
              operation: 'finalizeChunkedUpload',
              uploadId: uploadId,
              folderName: folderName,
              fileName: image.name,
              contentType: image.type
            })
          });
          
          if (!finalizeResponse.ok) {
            const errorData = await finalizeResponse.json();
            throw new Error(`API error: ${errorData.error || finalizeResponse.statusText}`);
          }
          
          const finalizeResult = await finalizeResponse.json();
          console.log('Chunked upload finalized:', finalizeResult);
          
          uploadedFiles++;
          setUploadProgress(Math.round((uploadedFiles / totalFiles) * 100));
          console.log(`Progress: ${uploadedFiles}/${totalFiles} (${Math.round((uploadedFiles / totalFiles) * 100)}%)`);
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          console.error('Error details:', JSON.stringify(uploadError, null, 2));
          console.error('Upload failed for image:', fileName);
          throw new Error(`Failed to upload image ${image.name}: ${uploadError instanceof Error ? uploadError.message : String(uploadError)}`);
        }
      }
      
      // Create and upload data file
      const dataFileName = `${folderName}/${folderName}.data`;
      console.log(`Preparing to upload data file: ${dataFileName}`);
      
      try {
        // Prepare request payload for data file using uploadJson operation
        const payload = {
          operation: 'uploadJson',
          folderName: folderName,
          fileName: `${folderName}.data`,
          content: formData
        };
        
        console.log('Calling S3 API endpoint for data file...');
        const response = await fetch(`${API_URL}/s3`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken.toString()}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`API error: ${errorData.error || response.statusText}`);
        }
        
        const result = await response.json();
        console.log('Data file upload successful:', result);
        uploadedFiles++;
        setUploadProgress(Math.round((uploadedFiles / totalFiles) * 100));
        console.log(`Progress: ${uploadedFiles}/${totalFiles} (${Math.round((uploadedFiles / totalFiles) * 100)}%)`);
      } catch (uploadError) {
        console.error('Error uploading data file:', uploadError);
        console.error('Error details:', JSON.stringify(uploadError, null, 2));
        console.error('Upload failed for data file:', dataFileName);
        throw new Error(`Failed to upload data file: ${uploadError instanceof Error ? uploadError.message : String(uploadError)}`);
      }
      
      // Reset form and state
      setFormData({
        id: '',
        name: '',
        isFeature: 'no',
        isInGallery: 'no',
        sqr_feet: '',
        highlight: 'NONE',
        type: '',
        completion_year: '',
        location: '',
        project_details: ''
      });
      
      setSelectedImages([]);
      setSuccess('Project created successfully!');
    } catch (err) {
      console.error('Error uploading files:', err);
      setError(`Failed to upload files: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div 
      className="min-h-screen p-4"
      style={{
        backgroundImage: 'url("/images/admin/admin-background-elegant.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Navigation Bar */}
      <nav className="max-w-6xl mx-auto mb-6">
        <div className="flex justify-center gap-2">
          <Link
            to="/"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            🏠 Home
          </Link>
          <Link
            to="/admin/create-project"
            className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 ring-2 ring-blue-500"
          >
            🎨 Create Project
          </Link>
          <Link
            to="/file-sharing"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            📁 File Sharing
          </Link>
          <Link
            to="/admin"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            ⚙️ Admin
          </Link>
        </div>
      </nav>

      <header className="mb-8 text-center animate-[fadeIn_0.5s_ease-out]">
        <div className="flex justify-center mb-4">
          <img src="/images/admin/ed-character-logo.png" alt="Ed Character Logo" className="h-32" />
        </div>
        <h1 className="text-4xl font-bold mb-2 text-primary drop-shadow-md">Create Web Project</h1>
        <p className="text-lg text-muted-foreground mb-4">
          Add a new project to the website
        </p>
        <div className="bg-white/70 backdrop-blur-sm py-2 px-6 rounded-full inline-block">
          <p className="text-primary font-medium">
            Fill in the project details and upload images
          </p>
        </div>
      </header>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 animate-[fadeIn_0.3s_ease-out]">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 animate-[fadeIn_0.3s_ease-out]">
          {success}
        </div>
      )}
      
      <div className="glass-effect rounded-xl shadow-xl p-6 mb-6 border border-white/20 backdrop-blur-md animate-[fadeIn_0.6s_ease-out]">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Form Fields */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-primary mb-4">Project Information</h2>
            
            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                required
              />
            </div>
            
            {/* Is Feature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feature Project
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isFeature"
                    value="yes"
                    checked={formData.isFeature === 'yes'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isFeature"
                    value="no"
                    checked={formData.isFeature === 'no'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>
            
            {/* Is In Gallery */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display in Gallery
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isInGallery"
                    value="yes"
                    checked={formData.isInGallery === 'yes'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isInGallery"
                    value="no"
                    checked={formData.isInGallery === 'no'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>
            
            {/* Square Footage */}
            <div>
              <label htmlFor="sqr_feet" className="block text-sm font-medium text-gray-700 mb-1">
                Square Footage
              </label>
              <input
                type="number"
                id="sqr_feet"
                name="sqr_feet"
                value={formData.sqr_feet}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
              />
            </div>
            
            {/* Highlight/Award */}
            <div>
              <label htmlFor="highlight" className="block text-sm font-medium text-gray-700 mb-1">
                Highlight/Award
              </label>
              <input
                type="text"
                id="highlight"
                name="highlight"
                value={formData.highlight}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                placeholder="NONE"
              />
            </div>
            
            {/* Project Type */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                Project Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                required
              >
                <option value="">Select a type</option>
                <option value="Workplace Design">Workplace Design</option>
                <option value="Industrial">Industrial</option>
                <option value="Medical">Medical</option>
                <option value="Restaurant">Restaurant</option>
                <option value="Retail">Retail</option>
              </select>
            </div>
            
            {/* Completion Year */}
            <div>
              <label htmlFor="completion_year" className="block text-sm font-medium text-gray-700 mb-1">
                Completion Year
              </label>
              <input
                type="number"
                id="completion_year"
                name="completion_year"
                value={formData.completion_year}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                min="1900"
                max="2100"
              />
            </div>
            
            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
              />
            </div>
            
            {/* Project Details */}
            <div>
              <label htmlFor="project_details" className="block text-sm font-medium text-gray-700 mb-1">
                Project Details
              </label>
              <textarea
                id="project_details"
                name="project_details"
                value={formData.project_details}
                onChange={handleInputChange}
                rows={5}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all resize-y"
              />
            </div>
          </div>
          
          {/* Right Column - Image Upload */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-primary mb-4">Project Images</h2>
            
            {/* Image Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white/50">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                multiple
                accept="image/*"
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer block"
              >
                <div className="flex flex-col items-center justify-center py-4">
                  <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <p className="text-gray-600 mb-1">Click to upload images</p>
                  <p className="text-gray-500 text-sm">Images must be in landscape format</p>
                </div>
              </label>
            </div>
            
            {/* Image Previews */}
            {selectedImages.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700">Selected Images</h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-40 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="bg-red-500 text-white p-2 rounded-full"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                      {image.width && image.height && image.width < image.height && (
                        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1 rounded-bl-lg">
                          Not landscape
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isUploading}
              >
                {isUploading ? 'Creating Project...' : 'Create Project'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWebProjectPage;
